import { useCurrentApplication, useFormatMessage } from 'app/common/hooks/';
import { get, isEmpty } from 'lodash';
import messages from './useInventoryMessaging.messages';
import { useContentInfo, useContentModel } from 'app/content/hooks';
import { useMemo } from 'react';

export const dealerInStockLabel = 'inStockLabel';
export const dealerOutOfStockLabel = 'outOfStockLabel';
export const pdcInStockLabel = 'pdcInStockLabel';
export const pdcOutOfStockLabel = 'pdcOutOfStockLabel';
export const networkInStockLabel = 'networkInStockLabel';

/**
 * Hook to manage inventory messaging content retrieval and caching.
 * This hook should be called at the beginning of any page that will need inventory messages content.
 * Use `useInventoryMessageContent`, `useGetInventoryMessageForProduct`, or `useInventoryAvailabilityLabel` to resolve the appropriate content in the appropriate component on the page.
 * */
const useInventoryMessaging = () => {
  const { inventoryMessagingModel: modelName } = useContentInfo();
  useContentModel(modelName);
};

function useFallbackMessages() {
  const formatMessage = useFormatMessage();
  return useMemo(() => {
    return {
      fields: {
        inStockLabel: {
          name: 'inStockLabel',
          value: formatMessage(messages.availabilityFallback, {
            isAvailable: true
          })
        },
        outOfStockLabel: {
          name: 'outOfStockLabel',
          value: formatMessage(messages.availabilityFallback, {
            isAvailable: false
          })
        }
      }
    };
  }, [formatMessage]);
}

/**
 * Get the appropriate inventory availability message for the given field. Fallback to the default in stock message if not available.
 *
 * @param inventoryMessagesContent The ContentItem with inventory message values
 * @param fieldName The message field name to use.
 * @param inStock Indicate if the fallback should be an in-stock or out-of-stock message
 * @returns {*} Inventory availability string appropriate for display
 */
export const useInventoryAvailabilityLabel = (
  inventoryMessagesContent,
  fieldName,
  inStock = false
) => {
  const fallbackValues = useFallbackMessages();
  return get(
    inventoryMessagesContent,
    buildFieldPath(fieldName),
    inStock
      ? getFallbackInStockMessage(fallbackValues)
      : getFallbackOutOfStockMessage(fallbackValues)
  );
};

/**
 * Monitor the content cache for the Inventory Messaging model and return it once it is available.
 */
export function useInventoryMessageContent() {
  const { inventoryMessagingModel: name } = useContentInfo();
  const fallbackValues = useFallbackMessages();

  const { content } = useContentModel(name, {
    fallbackValues
  });

  return content;
}

export const useGetInventoryMessageForProduct = product => {
  const inventoryMessagesContent = useInventoryMessageContent();

  const { identifierValue, preferredPdc } = useCurrentApplication();
  const available = get(product, 'inventorySummary.available', false);
  const hasQuantity = get(product, 'inventoryQty', 0) > 0;
  const isAvailable = available || hasQuantity;
  const locationQtys = get(product, 'inventorySummary.locationQuantities', {});
  const fallbackValues = useFallbackMessages();

  return useMemo(() => {
    if (inventoryMessagesContent) {
      return getInventoryMessage(
        inventoryMessagesContent,
        isAvailable,
        locationQtys,
        identifierValue,
        preferredPdc,
        fallbackValues
      );
    }
    return undefined;
  }, [
    inventoryMessagesContent,
    isAvailable,
    locationQtys,
    identifierValue,
    preferredPdc,
    fallbackValues
  ]);
};

function buildFieldPath(messageField) {
  return 'fields.' + messageField + '.value';
}

function getFallbackInStockMessage(fallbackValues) {
  return get(fallbackValues, buildFieldPath(dealerInStockLabel));
}

function getFallbackOutOfStockMessage(fallbackValues) {
  return get(fallbackValues, buildFieldPath(dealerOutOfStockLabel));
}

/**
 * Gets the appropriate inventory message for a product with given availability and locationQtys
 *
 * @param inventoryMessagesContent The ContentItem with inventory message values
 * @param isAvailable Indicate if the active dealer has the product in stock
 * @param locationQtys Map of location keys to quantity, for network and PDC inventory values
 * @param identifierValue The active dealer's location id
 * @param preferredPdc The dealer's preferred PDC location id
 * @param fallbackValues Messages to use if the given inventoryMessagesContent does not have a value for a field
 * @returns {*} Inventory availability string appropriate for display
 */
function getInventoryMessage(
  inventoryMessagesContent,
  isAvailable,
  locationQtys,
  identifierValue,
  preferredPdc,
  fallbackValues
) {
  if (isAvailable) {
    const dealerInStockFieldPath = buildFieldPath(dealerInStockLabel);
    return get(
      inventoryMessagesContent,
      dealerInStockFieldPath,
      getFallbackInStockMessage(fallbackValues)
    );
  } else {
    // Check for Dealer Network stock
    // Check for any location keys starting with "D-" and not matching identifierValue
    const hasNetworkStock = Object.keys(locationQtys || {}).some(
      key =>
        key.startsWith('D-') && key !== identifierValue && locationQtys[key] > 0
    );
    if (hasNetworkStock) {
      const networkFieldPath = buildFieldPath(networkInStockLabel);
      return get(
        inventoryMessagesContent,
        networkFieldPath,
        getFallbackOutOfStockMessage(fallbackValues)
      );
    }

    // Check for PDC stock
    if (get(locationQtys, preferredPdc, 0) > 0) {
      const pdcFieldPath = buildFieldPath(pdcInStockLabel);
      return get(
        inventoryMessagesContent,
        pdcFieldPath,
        getFallbackOutOfStockMessage(fallbackValues)
      );
    }

    // Show Out of Stock
    const dealerOutOfStockFieldPath = buildFieldPath(dealerOutOfStockLabel);
    return get(
      inventoryMessagesContent,
      dealerOutOfStockFieldPath,
      getFallbackOutOfStockMessage(fallbackValues)
    );
  }
}

export default useInventoryMessaging;
